import React from "react";
import {
  Divider,
  DatePicker,
  Select,
  Button,
  Modal,
  Table,
  Tag,
  Progress,
  Popconfirm,
  Skeleton,
  Input,
  message,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  PlusOutlined,
  ShoppingOutlined,
  UserOutlined,
  CarOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  fetchReports,
  fetchReportById,
  deleteReport,
  generateProductReport,
} from "../utils/fetchReports";
import * as XLSX from "xlsx";

const Reports = () => {
  const [reportType, setReportType] = useState("");
  const [unfinishedReports, setUnfinishedReports] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportName, setReportName] = useState("");
  const [reportStartDate, setReportStartDate] = useState(null);
  const [reportEndDate, setReportEndDate] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [refreshReports, setRefreshReports] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(null);

  const navigate = useNavigate();

  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleCancel = () => {
    setIsModalOpen(false);

    // Reset all fields of the form
    setReportType("");
    setReportStartDate(null);
    setReportEndDate(null);
    setReportName("");
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedReports(selectedRows);
    },
    getCheckboxProps: (record) => ({
      disabled: record.status !== "completed",
    }),
  };

  const handleDeleteReports = () => {
    // Delete the reports from the database
    const deleteReports = async () => {
      setLoading(true);
      for (const report of selectedReports) {
        await deleteReport(report.id);
      }
      setSelectedReports([]);
      setRefresh(!refresh);
      setLoading(false);
    };
    deleteReports();
  };

  const handleDownloadReport = async (reportId) => {
    // Generate an excel file with 5 columns: Product, Variant, Quantity
    setLoadingDownload(reportId);
    // The data should be fetched from the reportid
    const report = await fetchReportById(reportId);
    const data = report.details;
    const rows = data.map((item) => ({
      Producto: item.product_name,
      Variante: item.variant_name,
      Cantidad: item.quantity,
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.write(workbook, { bookType: "xlsx", type: "binary" });
    XLSX.writeFile(
      workbook,
      `${report.report_name}_${report.created_at.split("T")[0]}.xlsx`
    );
    setLoadingDownload(null);
  };

  const checkUnfinishedReports = async (baseReports = null) => {
    const unfinishedReportsInternal = baseReports
      ? baseReports.filter((item) => item.status !== "completed")
      : reports.filter((item) => item.status !== "completed");
    console.log("unfinishedReports", unfinishedReportsInternal);

    if (unfinishedReportsInternal.length === 0) {
      return;
    }

    // Update the status of each unfinished report
    for (const report of unfinishedReportsInternal) {
      try {
        console.log("updating the status of report", report.id);
        const reportData = await fetchReportById(report.id);
        const newReports = baseReports
          ? baseReports.map((item) =>
              item.id === reportData.id ? reportData : item
            )
          : reports.map((item) =>
              item.id === reportData.id ? reportData : item
            );
        setReports(newReports);
      } catch (error) {
        console.error(`Error updating report ${report.id}:`, error);
      }
    }
    setUnfinishedReports(unfinishedReportsInternal);

    // Schedule next check after 3 seconds
    await new Promise((resolve) => setTimeout(resolve, 3000));
    console.log("updating here inside the refresh reports");
    setRefreshReports((prev) => !prev);
  };

  // Hard reset of reports
  useEffect(() => {
    setLoading(true);
    const reports = async () => {
      const reports = await fetchReports();

      // Adding a column to flag if the report is selected
      const reportsFormated = reports.map((report) => {
        return { ...report, selected: false };
      });
      console.log("reportsFormated", reportsFormated);
      setReports(reportsFormated);
      setSelectedReports([]);
      checkUnfinishedReports(reportsFormated);
      setLoading(false);
    };
    reports();
  }, [refresh]);

  // "id": 36,
  // "report_name": "Testing",
  // "status": "initializing",
  // "status_percentage": "0",
  // "object_type": "product",
  // "start_date": "2025-05-24T04:00:00.000Z",
  // "end_date": "2025-05-24T04:00:00.000Z",
  // "current_date_calculation": "2025-05-24T04:00:00.000Z",
  // "created_at": "2025-05-26T04:00:00.000Z",
  // "updated_at": "2025-05-26T04:00:00.000Z"

  const columns = [
    {
      title: "Nombre del Reporte",
      dataIndex: "report_name",
      key: "report_name",
      width: 150,
    },
    {
      title: "Tipo de Reporte",
      dataIndex: "object_type",
      key: "object_type",
      width: 100,
      render: (text) => objectTypes[text].component,
    },
    {
      title: "Fecha de Creación",
      dataIndex: "created_at",
      key: "created_at",
      width: 100,
      render: (text) => <div>{text.split("T")[0]}</div>,
    },
    {
      title: "Rango de Fechas",
      dataIndex: "end_date",
      key: "end_date",
      width: 180,
      render: (text, record) => (
        <div>
          {record.start_date.split("T")[0]} a {text.split("T")[0]}
        </div>
      ),
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (text, record) => {
        return statuses(record.status_percentage)[text].component;
      },
    },
    {
      title: "Acciones",
      dataIndex: "actions",
      key: "actions",
      width: 150,
      render: (text, record) => (
        <div className="flex flex-col justify-end items-center gap-2">
          <Button
            type="dashed"
            icon={<DownloadOutlined />}
            size="small"
            disabled={
              record.status !== "completed" ||
              loading ||
              (loadingDownload !== null &&
                loadingDownload !== record.id &&
                loadingDownload !== record.id)
            }
            onClick={() => handleDownloadReport(record.id)}
            loading={loadingDownload === record.id}
          >
            Descargar
          </Button>
          <Link
            to={`/reports/${record.id}`}
            disabled={record.status !== "completed" || loading}
            className={`${record.status !== "completed" ? "hidden" : "block"}`}
          >
            Ver Detalles
          </Link>
        </div>
      ),
    },
  ];

  const skeletonColumns = columns.map((column) => ({
    ...column,
    render: (col) => {
      return <Skeleton active title={false} paragraph={{ rows: 1 }} />;
    },
  }));

  // Do this useEffect until all projects are "completed". Using the interval of 1 second.
  useEffect(() => {
    let timeoutId = null;

    // Initial check
    checkUnfinishedReports();

    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [refreshReports]);

  return (
    <div>
      <div className="font-bold text-3xl">Reportes</div>
      <Divider />
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold ">Lista de Reportes</h2>
            <div className="flex gap-2">
              <Button
                type="dashed"
                onClick={() => {
                  showModal(true);
                }}
              >
                <PlusOutlined /> Nuevo Reporte
              </Button>
              <Popconfirm
                title="¿Estás seguro de querer eliminar estos reportes?"
                onConfirm={handleDeleteReports}
                okText="Eliminar"
                cancelText="Cancelar"
                okButtonProps={{
                  className: "custom-confirm-delete-button",
                  style: {
                    backgroundColor: "#f5222d",
                    borderColor: "#f5222d",
                    color: "#fff",
                  },
                }}
              >
                <Button
                  type="link"
                  icon={<DeleteOutlined />}
                  danger
                  disabled={selectedReports.length === 0}
                  loading={loading}
                >
                  Eliminar
                </Button>
              </Popconfirm>
            </div>
          </div>
          <Modal
            open={isModalOpen}
            onCancel={handleCancel}
            footer={null}
            width={1000}
          >
            <div className="min-h-[400px]">
              <h2 className="text-lg">Selecciona un tipo de reporte</h2>
              <div className="mt-2 w-full">
                <Select
                  className="min-w-36 sm:w-full md:w-auto"
                  options={reportTypes}
                  onChange={(value) => setReportType(value)}
                  value={reportType}
                />
              </div>
              <Divider />
              {reportType &&
                reportForms(
                  isModalOpen,
                  setIsModalOpen,
                  reportStartDate,
                  setReportStartDate,
                  reportEndDate,
                  setReportEndDate,
                  refresh,
                  setRefresh,
                  reportName,
                  setReportName,
                  setRefreshReports,
                  unfinishedReports,
                  setUnfinishedReports,
                  setReports
                )[reportType]}
            </div>
          </Modal>
          {/* Table with all the reports */}
          <div>
            {/* Put skeleton inside the table while loading */}
            {loading ? (
              <Table
                columns={skeletonColumns}
                dataSource={Array.from({ length: 3 }).map((_, index) => ({
                  key: index,
                }))}
                pagination={false}
              />
            ) : (
              <Table
                disabled={loading}
                columns={columns}
                dataSource={reports.map((item, id) => {
                  item.key = id;
                  return item;
                })}
                rowSelection={{
                  type: "checkbox",
                  ...rowSelection,
                }}
                scroll={{ y: "calc(100vh - 450px)" }}
                pagination={{
                  pageSize: 6,
                  position: ["topRight"],
                }}
                sticky={true}
                rowKey="id"
                loading={loading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;

const reportTypes = [
  { label: "Productos", value: "products" },
  { label: "Pedidos", value: "orders", disabled: true },
  { label: "Vendedores", value: "sellers", disabled: true },
  { label: "Choferes", value: "drivers", disabled: true },
];

const ProductReportForm = ({
  isModalOpen,
  setIsModalOpen,
  reportStartDate,
  setReportStartDate,
  reportEndDate,
  setReportEndDate,
  reportName,
  setReportName,
  refresh,
  setRefresh,
  setRefreshReports,
  unfinishedReports,
  setUnfinishedReports,
  setReports,
}) => {
  const handleGenerateReport = async () => {
    if (!reportName) {
      message.error("Por favor, ingrese un nombre para el reporte");
      return;
    }
    console.log("the report has started its generation");
    const report = generateProductReport(
      reportStartDate,
      reportEndDate,
      reportName
    ); // doesnt need to be await because it is not a promise
    console.log("report just generated", report);
    setIsModalOpen((prev) => {
      console.log("updating here inside the setIsModalOpen");
      return false;
    });
    setRefresh((prev) => {
      console.log("updating here inside the setRefresh");
      return !prev;
    });
    setRefreshReports((prev) => {
      console.log("updating here inside the setRefreshReports");
      return !prev;
    });

    const newReports = await fetchReports();
    const reportsFormated = newReports.map((report) => {
      return { ...report, selected: false };
    });
    setReports(reportsFormated);
    setUnfinishedReports(
      reportsFormated.filter((report) => report.status !== "completed")
    );
    return;
  };

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-lg font-bold">Reporte de Productos</h2>
      <div>Selecciona una fecha de inicio y una fecha de fin</div>
      <div className="mt-2">
        <div className="flex flex-col gap-2">
          <div className="text-sm">Nombre del Reporte</div>
          <Input
            className="w-full"
            placeholder="Nombre del Reporte"
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2 mt-4">
          <div className="text-sm">Rango de Fechas</div>
          <DatePicker.RangePicker
            onChange={(dates) => {
              if (dates) {
                setReportStartDate(dates[0]);
                setReportEndDate(dates[1]);
              }
            }}
          />
        </div>
        <div className="flex justify-end mt-4">
          <Button
            type="primary"
            onClick={handleGenerateReport}
            className="mt-4"
          >
            Generar Reporte
          </Button>
        </div>
      </div>
    </div>
  );
};

const OrderReportForm = () => {
  return (
    <div>
      <h2 className="text-lg">Reporte de Pedidos</h2>
    </div>
  );
};

const SellerReportForm = () => {
  return (
    <div>
      <h2 className="text-lg">Reporte de Vendedores</h2>
    </div>
  );
};

const DriverReportForm = () => {
  return (
    <div>
      <h2 className="text-lg">Reporte de Choferes</h2>
    </div>
  );
};

const reportForms = (
  isModalOpen,
  setIsModalOpen,
  reportStartDate,
  setReportStartDate,
  reportEndDate,
  setReportEndDate,
  refresh,
  setRefresh,
  reportName,
  setReportName,
  setRefreshReports,
  unfinishedReports,
  setUnfinishedReports,
  setReports
) => ({
  products: (
    <ProductReportForm
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      reportStartDate={reportStartDate}
      setReportStartDate={setReportStartDate}
      reportEndDate={reportEndDate}
      setReportEndDate={setReportEndDate}
      refresh={refresh}
      setRefresh={setRefresh}
      reportName={reportName}
      setReportName={setReportName}
      setRefreshReports={setRefreshReports}
      unfinishedReports={unfinishedReports}
      setUnfinishedReports={setUnfinishedReports}
      setReports={setReports}
    />
  ),
  orders: <OrderReportForm />,
  sellers: <SellerReportForm />,
  drivers: <DriverReportForm />,
});

const objectTypes = {
  product: {
    label: "Producto",
    component: (
      <Tag>
        <ShoppingOutlined /> Producto
      </Tag>
    ),
  },
  order: { label: "Pedido", icon: <ShoppingOutlined /> },
  seller: { label: "Vendedor", icon: <UserOutlined /> },
  driver: { label: "Chofer", icon: <CarOutlined /> },
};

export const statuses = (status_percentage) => {
  return {
    initializing: {
      label: "Inicializando",
      component: <Tag color="yellow">Inicializando</Tag>,
    },
    in_progress: {
      label: "Procesando",
      component: (
        <div>
          <Progress percent={Math.round(status_percentage)} status="active" />
        </div>
      ),
    },
    completed: {
      label: "Completado",
      component: <Tag color="green">Completado</Tag>,
    },
    failed: { label: "Fallado", component: <Tag color="red">Fallado</Tag> },
  };
};
